/**
 * Vero, das Maskottchen: hält Gesagtes fest und schlägt das passende Projekt vor, sucht in den eigenen Daten
 * und beantwortet Fragen zur Bedienung und zum Konto. Läuft ohne fremden KI-Dienst.
 */

export interface AssistantSuggestion {
  projectId: string;
  projectName: string;
  customer: string | null;
  /** Warum dieses Projekt, z. B. „Kunde Schneider“. */
  reason: string;
}

export type AssistantHitKind = "project" | "folder" | "file" | "note" | "event" | "device" | "customer";

export interface AssistantHit {
  kind: AssistantHitKind;
  id: string;
  title: string;
  sub: string;
  projectId: string | null;
  folderId: string | null;
}

export interface AssistantAnswer {
  /** Klingt das nach einer Frage? Dann zuerst Hilfe und Treffer zeigen, sonst zuerst „wohin damit“. */
  question: boolean;
  suggestions: AssistantSuggestion[];
  hits: AssistantHit[];
  help: HelpArticle[];
}

export interface HelpArticle {
  id: string;
  title: string;
  text: string;
  /** Wohin der Knopf führt: Bereich der App, z. B. "konto" oder "kalender". */
  /** Bebilderter Rundgang zum Thema; Vero bietet ihn zum Durchklicken an. */
  guide?: "start" | "rechnungen";
  area?: "projekte" | "notizen" | "kalender" | "handy" | "konto" | "kundenbereich";
  keywords: string[];
}

export const HELP_ARTICLES: HelpArticle[] = [
  {
    id: "rundgang",
    title: "Rundgang durch HandVero mit Bildern",
    text: "Fünf kurze Bildschritte zeigen Projekte, Fotos, Notizen, Termine und mich. Sie lassen sich jederzeit überspringen und später unter „Mein Konto“ wieder öffnen.",
    guide: "start",
    keywords: ["rundgang", "einführung", "erklärung", "erklären", "anleitung", "bilder", "tour", "zeigen", "zeig", "erste", "schritte", "anfangen", "starten", "überblick", "hilfe"],
  },
  {
    id: "rechnungen-rundgang",
    title: "Rechnungen und Belege mit Bildern erklärt",
    text: "Mit Bildern: vom Angebot zur Rechnung, die PDF mit Zahl-QR-Code, Übersicht, Ausgaben und Bank. Das gilt für Firmen, bei denen das Paket „Rechnungen und Belege“ freigeschaltet ist.",
    guide: "rechnungen",
    keywords: ["rechnung", "rechnungen", "angebot", "angebote", "mahnung", "ausgaben", "belege", "beleg", "bank", "qr", "e-rechnung", "paket", "zusatzpaket", "zusatzmodul"],
  },
  {
    id: "diktat",
    title: "Angebot per Sprache oder Text erstellen",
    text: "Am PC unter Angebote (oder Aufträge, Rechnungen) auf „Per Sprache oder Text“. Sagen oder schreiben Sie zum Beispiel: Angebot an Herrn Sülze, Stundenbasis 8 Stunden, Installationsrohr 32 Euro. HandVero macht daraus einen Entwurf mit Kunde und Preisen aus Ihren gespeicherten Daten. Was fehlt, ist als „noch ausfüllen“ markiert. Den Entwurf können Sie jederzeit speichern, den Beleg anlegen erst, wenn alles ausgefüllt ist. Das gilt für Firmen mit dem Paket „Rechnungen und Belege“.",
    keywords: ["diktat", "diktieren", "sprechen", "sagen", "spracheingabe", "sprache", "angebot", "angebote", "entwurf", "stundenbasis"],
  },
  {
    id: "protokolle",
    title: "Protokolle aus Vorlagen",
    text: "Im Projekt auf „Protokolle“ und „Neues Protokoll“. Vorlagen gibt es für Prüfprotokolle (Anlage, Geräte, PV, Ladepunkt, Thermografie), Unterweisungen, Belehrungen, Gefährdungsbeurteilungen, Leitern, Abnahmen und Wartungen. Ausfüllen, auf dem Handy unterschreiben lassen, abschließen: Das PDF landet im passenden Ordner. Ob ein Protokoll im Einzelfall rechtlich genügt, prüfen Sie selbst.",
    area: "projekte",
    keywords: ["protokoll", "protokolle", "prüfprotokoll", "prüfbericht", "unterweisung", "belehrung", "gefährdungsbeurteilung", "abnahme", "abnahmeprotokoll", "wartung", "unterschrift", "unterschreiben", "messprotokoll", "dguv"],
  },
  {
    id: "schulungen",
    title: "Online-Schulungen",
    text: "In der Windows-App links auf „Schulungen“. Jeder Abschnitt zeigt erst einen kurzen Film, dann kommen Fragen. Wer etwas falsch beantwortet, sieht den Abschnitt noch einmal. Am Ende unterschreiben Sie auf dem Bildschirm und erhalten den Teilnahmenachweis als PDF. Inhaber und Admins ergänzen eigene Hinweise und Videos und sehen, wer wieder an der Reihe ist.",
    keywords: ["schulung", "schulungen", "online-schulung", "e-learning", "elearning", "lernen", "kurs", "teilnahmenachweis", "teilnahmebescheinigung", "zertifikat"],
  },
  {
    id: "projekt-anlegen",
    title: "Projekt anlegen",
    text: "Unter Projekte auf „Neues Projekt“. Name, Kunde und Adresse eintragen und eine Vorlage wählen, dann sind die Ordner gleich da. Ordner lassen sich jederzeit umbenennen, verschieben und ergänzen.",
    area: "projekte",
    keywords: ["projekt", "auftrag", "anlegen", "neu", "kunde", "kunden", "baustelle", "vorlage", "ordner"],
  },
  {
    id: "fotos",
    title: "Fotos direkt in den Ordner",
    text: "Auf dem Handy das Projekt und den Ordner öffnen und unten auf die Kamera tippen. Das Foto landet sofort im Ordner und ist am PC zu sehen. „Weiter fotografieren“ bringt Sie zurück in den zuletzt genutzten Ordner.",
    area: "projekte",
    keywords: ["foto", "fotos", "bild", "bilder", "kamera", "aufnehmen", "fotografieren"],
  },
  {
    id: "hochladen",
    title: "Dateien am PC hochladen",
    text: "Dateien einfach aus dem Explorer in einen Ordner ziehen oder „Dateien hochladen“ wählen. Fotos und Dokumente lassen sich per Ziehen in andere Ordner verschieben. Erlaubt sind Fotos, PDFs, Office-Dateien, E-Mails (.eml, .msg), Pläne und ZIP bis 50 MB.",
    area: "projekte",
    keywords: ["hochladen", "upload", "datei", "dateien", "pdf", "dokument", "dokumente", "ziehen", "verschieben", "mail", "mails", "email"],
  },
  {
    id: "mails",
    title: "E-Mails ablegen und finden",
    text: "E-Mails speichern Sie aus Outlook als Datei (.msg oder .eml) und ziehen sie in den passenden Ordner. Danach finde ich sie über den Dateinamen. Eine direkte Verbindung zum Postfach gibt es noch nicht.",
    area: "projekte",
    keywords: ["mail", "mails", "email", "e-mail", "postfach", "outlook", "nachricht"],
  },
  {
    id: "handy",
    title: "Handy verknüpfen",
    text: "Am PC unter „Handy“ erscheint ein QR-Code. Mit der Kamera des Handys scannen und den Link antippen, dann ist das Handy ohne Passwort angemeldet. Im Browser „Zum Home-Bildschirm“ wählen, dann startet HandVero wie eine App.",
    area: "handy",
    keywords: ["handy", "smartphone", "iphone", "android", "qr", "verknüpfen", "app", "installieren", "home-bildschirm"],
  },
  {
    id: "notizen",
    title: "Notizen und Sprachmemos",
    text: "Unter Notizen schreiben oder auf das Mikrofon tippen und sprechen (bis 10 Minuten). Notizen lassen sich einem Projekt zuordnen. Mir können Sie auch einfach etwas sagen oder schreiben, dann schlage ich das passende Projekt vor.",
    area: "notizen",
    keywords: ["notiz", "notizen", "sprachmemo", "sprache", "diktieren", "aufnahme", "mikrofon", "merken", "festhalten"],
  },
  {
    id: "kalender",
    title: "Kalender und Outlook",
    text: "Im Kalender Termine anlegen und einer Person zuordnen. Mit „Mit Outlook verbinden“ landen Ihre Termine in Ihrem Outlook-Kalender und Termine aus Outlook erscheinen hier. Das verbindet jede Person für sich, trennen geht jederzeit.",
    area: "kalender",
    keywords: ["kalender", "termin", "termine", "outlook", "microsoft", "verbinden", "abgleich", "synchronisieren"],
  },
  {
    id: "mitarbeiter",
    title: "Mitarbeiter einladen und Rollen",
    text: "Im Kundenbereich unter Mitarbeiter eine E-Mail-Adresse eintragen. Die Person bekommt eine Einladung und legt ihr eigenes Passwort fest. Der Inhaber kann Mitarbeiter zu Admins machen; Admins dürfen zusätzlich einladen, Projekte löschen und festlegen, wer welches Projekt sieht.",
    area: "kundenbereich",
    keywords: ["mitarbeiter", "einladen", "einladung", "kollege", "team", "rolle", "rollen", "admin", "rechte", "zugang", "zugänge"],
  },
  {
    id: "sichtbarkeit",
    title: "Wer sieht welches Projekt?",
    text: "Neue Projekte sehen alle in der Firma. Inhaber und Admins können ein Projekt auf ausgewählte Mitarbeiter beschränken: im Projekt oben auf „…“ und „Wer sieht das Projekt?“. Fotos, Dateien, Notizen und Termine des Projekts sind dann für andere unsichtbar.",
    area: "projekte",
    keywords: ["sichtbar", "sieht", "freigabe", "freigaben", "zugriff", "berechtigung", "rechte", "beschränken", "geheim", "vertraulich"],
  },
  {
    id: "konto",
    title: "Mein Konto: Name, Passwort, Geräte",
    text: "Unter „Mein Konto“ ändern Sie Ihren Namen und Ihr Passwort und sehen, auf welchen Geräten Sie angemeldet sind. Ein verlorenes Handy melden Sie dort mit einem Klick ab.",
    area: "konto",
    keywords: ["konto", "profil", "passwort", "name", "gerät", "geräte", "abmelden", "verloren", "gestohlen", "sicherheit"],
  },
  {
    id: "konto-loeschen",
    title: "Konto löschen",
    text: "Unter „Mein Konto“ ganz unten auf „Konto löschen“ und mit dem Passwort bestätigen. Ihre Fotos und Dokumente bleiben in der Firma. Als Inhaber löschen Sie Ihr Konto zusammen mit der ganzen Firma, dabei werden alle Projekte, Fotos und Dokumente endgültig gelöscht.",
    area: "konto",
    keywords: ["löschen", "loeschen", "konto", "account", "entfernen", "austreten", "firma", "daten", "kündigen"],
  },
  {
    id: "abo",
    title: "Paket und Abo",
    text: "Die ersten 100 Firmen nutzen HandVero mindestens drei Jahre kostenlos mit bis zu drei Zugängen. Ein kostenpflichtiges Abo gibt es derzeit nicht, deshalb muss auch nichts gekündigt werden. Ihr Paket sehen Sie im Kundenbereich.",
    area: "kundenbereich",
    keywords: ["abo", "abonnement", "kündigen", "kündigung", "beenden", "preis", "kosten", "paket", "bezahlen", "rechnung", "startaktion"],
  },
  {
    id: "support",
    title: "Hilfe vom HandVero-Support",
    text: "Schreiben Sie an service@handvero.de. Wenn der Support in Ihr Konto schauen soll, fragt er vorher an. Sie erlauben das im Kundenbereich für eine begrenzte Zeit und können es jederzeit beenden.",
    area: "kundenbereich",
    keywords: ["support", "hilfe", "kontakt", "problem", "fehler", "frage", "service"],
  },
  {
    id: "daten",
    title: "Wo liegen meine Daten?",
    text: "Ihre Daten liegen bei HandVero, streng getrennt von anderen Firmen. Verbindungen sind verschlüsselt, Passwörter werden nur als Prüfwert gespeichert. Ich, Vero, arbeite ohne fremden KI-Dienst: Was Sie mir sagen, bleibt bei HandVero. In der Startphase sichern Sie wichtige Daten bitte zusätzlich selbst.",
    keywords: ["daten", "datenschutz", "dsgvo", "sicher", "sicherheit", "server", "deutschland", "verschlüsselt", "ki"],
  },
  {
    id: "vero",
    title: "Vero ausblenden",
    text: "Mich blenden Sie unter „Darstellung“ aus. Dort schalten Sie mich auch wieder ein.",
    keywords: ["vero", "maskottchen", "ausblenden", "ausschalten", "abschalten", "weg", "stört"],
  },
  {
    id: "windows",
    title: "HandVero für Windows",
    text: "Die Windows-Software ist noch nicht verfügbar. Wir informieren registrierte Firmen per E-Mail, sobald es sie gibt. Bis dahin nutzen Sie HandVero mit der Web-App auf dem Handy.",
    area: "kundenbereich",
    keywords: ["windows", "pc", "computer", "download", "herunterladen", "installieren", "software", "programm"],
  },
];

const STOP = new Set(
  "der die das den dem des ein eine einen einem einer und oder aber auch noch nur mit von vom zum zur bei beim für fur auf aus ist sind war hat haben bitte mal ich du sie wir ihr es mir mich dir uns euch wie wo was wer wann warum wieso kann können konnen muss soll will möchte mochte gibt geht man hier dort da im in an am zu so dass nicht kein keine ja nein heute morgen gerade dann jetzt schon".split(
    " ",
  ),
);

/** Kleinbuchstaben, Umlaute vereinheitlicht, ohne Satzzeichen. */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Aussagekräftige Wörter einer Eingabe (ohne Füllwörter). Hausnummern und PLZ bleiben erhalten. */
export function keywords(input: string): string[] {
  const words = normalize(input).split(" ");
  return [...new Set(words.filter((w) => (w.length >= 3 || /^\d+$/.test(w)) && !STOP.has(w)))].slice(0, 12);
}

export function looksLikeQuestion(input: string): boolean {
  const t = input.trim().toLowerCase();
  return t.endsWith("?") || /^(wie|wo|was|wer|wann|warum|wieso|kann|können|darf|gibt|hilfe|zeig|such|finde)\b/.test(t);
}

/** Passende Hilfe-Artikel, bester zuerst. */
export function matchHelp(input: string, limit = 3): HelpArticle[] {
  const words = keywords(input);
  if (!words.length) return [];
  const scored = HELP_ARTICLES.map((a) => {
    const keys = a.keywords.map(normalize);
    const title = normalize(a.title);
    let score = 0;
    for (const w of words) {
      if (keys.some((k) => k === w)) score += 3;
      else if (keys.some((k) => k.startsWith(w) || w.startsWith(k))) score += 2;
      else if (title.includes(w)) score += 1;
    }
    return { a, score };
  });
  return scored
    .filter((x) => x.score >= 2)
    .sort((x, y) => y.score - x.score)
    .slice(0, limit)
    .map((x) => x.a);
}
